import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Container, Grid, Icon, Message } from "semantic-ui-react";
import { Heading1 } from "../../components/value-cssmodules/TextComponent";
import PatternBox from "../Patterns/PatternBox";
import FilterSidebar from "../PatternsList/FilterSidebar";
import Spinner from "../../components/Spinner/Spinner";
import { favouritesSelector } from "../../utils/selectors";
import api from "../../utils/api";
import { PatternData, PatternDifficulty } from "../../types/siteTypes";
import styles from "./Favourites.module.scss";

const normalizeDifficulty = (value: unknown): PatternDifficulty => {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (normalized === "easy" || normalized === "hard" || normalized === "medium") {
    return normalized;
  }
  return "medium";
};

const Favourites = () => {
  const favourites = useSelector((state: any) => favouritesSelector(state));
  const intl = useIntl();
  const [patterns, setPatterns] = useState<PatternData[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const searchPlaceholder = intl.formatMessage({
    id: "favourites.search.placeholder",
    defaultMessage: "Search favourite patterns",
  });

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [selectedFabrics, setSelectedFabrics] = useState<string[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState<PatternDifficulty | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const hasActiveFilters =
    selectedCategories.length > 0 ||
    selectedClients.length > 0 ||
    selectedFabrics.length > 0 ||
    selectedSizes.length > 0 ||
    !!selectedDifficulty ||
    searchQuery.trim().length > 0;

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedClients([]);
    setSelectedFabrics([]);
    setSelectedSizes([]);
    setSelectedDifficulty(null);
    setSearchQuery("");
  };

  const favouriteIds = useMemo(
    () => {
      const orderedFavourites = Array.isArray(favourites)
        ? [...favourites].reverse()
        : [];
      const seen = new Set();

      return orderedFavourites
        .map((item: any) => item?.id)
        .filter((id: any) => {
          if (!id || seen.has(id)) {
            return false;
          }
          seen.add(id);
          return true;
        });
    },
    [favourites]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchFavouritePatterns = async () => {
      if (favouriteIds.length === 0) {
        setPatterns([]);
        setFetchError("");
        return;
      }

      setLoading(true);
      setFetchError("");

      const requests = favouriteIds.map((id) =>
        api.get(`/patterns/id/${id}?displayCurrency=EUR`)
      );
      const results = await Promise.allSettled(requests);

      if (!isMounted) return;

      const loadedPatterns = results
        .filter((result): result is PromiseFulfilledResult<any> => result.status === "fulfilled")
        .map((result) => result.value?.data)
        .filter(Boolean);

      setPatterns(loadedPatterns);

      if (loadedPatterns.length === 0) {
        setFetchError(
          intl.formatMessage({
            id: "favourites.fetchError",
            defaultMessage: "Failed to load favourite patterns.",
          }),
        );
      }

      setLoading(false);
    };

    fetchFavouritePatterns();

    return () => {
      isMounted = false;
    };
  }, [favouriteIds, intl]);

  const filteredPatterns = useMemo(() => {
    const hasAny = (source: string[] = [], selected: string[]) =>
      selected.length === 0 || selected.some((value) => source.includes(value));

    return patterns.filter((pattern) => {
      const patternCategories = Array.isArray(pattern.categories)
        ? pattern.categories
        : [];
      const patternClients = Array.isArray(pattern.clients) ? pattern.clients : [];
      const patternFabrics = Array.isArray(pattern.fabrics) ? pattern.fabrics : [];
      const patternSizes = Array.isArray(pattern.sizes) ? pattern.sizes : [];
      const patternDifficulty = normalizeDifficulty(pattern.difficulty);
      const normalizedSearch = searchQuery.trim().toLowerCase();
      const matchesSearch =
        normalizedSearch.length === 0 ||
        String(pattern.name || "").toLowerCase().includes(normalizedSearch);
      const matchesDifficulty =
        !selectedDifficulty || patternDifficulty === selectedDifficulty;

      return (
        matchesSearch &&
        matchesDifficulty &&
        hasAny(patternCategories, selectedCategories) &&
        hasAny(patternClients, selectedClients) &&
        hasAny(patternFabrics, selectedFabrics) &&
        hasAny(patternSizes, selectedSizes)
      );
    });
  }, [
    patterns,
    selectedCategories,
    selectedClients,
    selectedFabrics,
    selectedSizes,
    selectedDifficulty,
    searchQuery,
  ]);
  const resultsCount = filteredPatterns.length;

  return (
    <div className={styles.favouritesPage}>
      <Container>
        <Heading1 className={styles.pageHeading}>
          <FormattedMessage
            id="favourites.page.title"
            defaultMessage="Ulubione wykroje"
          />
        </Heading1>
        <Grid>
          <Grid.Column width={4} only="computer">
            <FilterSidebar
              selectedCategories={selectedCategories}
              setSelectedCategories={setSelectedCategories}
              selectedClients={selectedClients}
              setSelectedClients={setSelectedClients}
              selectedFabrics={selectedFabrics}
              setSelectedFabrics={setSelectedFabrics}
              selectedSizes={selectedSizes}
              setSelectedSizes={setSelectedSizes}
              selectedDifficulty={selectedDifficulty}
              setSelectedDifficulty={setSelectedDifficulty}
              searchValue={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder={searchPlaceholder}
            />
          </Grid.Column>
          <Grid.Column mobile={16} tablet={16} only="tablet mobile" className={styles.mobileFiltersColumn}>
            <div className={styles.mobileFiltersHeader}>
              <button
                type="button"
                className={styles.mobileFiltersToggle}
                onClick={() => setMobileFiltersOpen((prev) => !prev)}
              >
                <span className={styles.mobileFiltersToggleLabel}>
                  <Icon name={mobileFiltersOpen ? "angle up" : "filter"} />
                  <FormattedMessage id="patterns.filters.title" defaultMessage="Filters" />
                </span>
              </button>
              <button
                type="button"
                className={styles.mobileFiltersClear}
                onClick={clearFilters}
                disabled={!hasActiveFilters}
              >
                <FormattedMessage
                  id="patterns.filters.clearFilters"
                  defaultMessage="Wyczyść filtry"
                />
              </button>
            </div>
              <div className={styles.mobileFiltersResults}>
                <span className={styles.mobileFiltersCount}>
                  <FormattedMessage
                    id="patterns.results.short"
                    defaultMessage="{count} results"
                    values={{ count: resultsCount }}
                  />
                </span>
              </div>
            {mobileFiltersOpen && (
              <div className={styles.mobileFiltersPanel}>
                <FilterSidebar
                  selectedCategories={selectedCategories}
                  setSelectedCategories={setSelectedCategories}
                  selectedClients={selectedClients}
                  setSelectedClients={setSelectedClients}
                  selectedFabrics={selectedFabrics}
                  setSelectedFabrics={setSelectedFabrics}
                  selectedSizes={selectedSizes}
                  setSelectedSizes={setSelectedSizes}
                  selectedDifficulty={selectedDifficulty}
                  setSelectedDifficulty={setSelectedDifficulty}
                  resultsCount={resultsCount}
                  searchValue={searchQuery}
                  onSearchChange={setSearchQuery}
                  searchPlaceholder={searchPlaceholder}
                  isSticky={false}
                  showHeader={false}
                />
              </div>
            )}
          </Grid.Column>

          <Grid.Column mobile={16} tablet={16} computer={12}>
            <main className={styles.mainSection}>
              {!loading && !fetchError && favouriteIds.length > 0 && (
                <div className={styles.resultsRow}>
                  <FormattedMessage
                    id="patterns.results.short"
                    defaultMessage="{count} results"
                    values={{ count: resultsCount }}
                  />
                </div>
              )}
              {loading ? (
                <Spinner />
              ) : fetchError ? (
                <Message negative>
                  <Message.Header>{fetchError}</Message.Header>
                </Message>
              ) : favouriteIds.length === 0 ? (
                <Message info>
                  <Message.Header>
                    <FormattedMessage
                      id="favourites.empty.title"
                      defaultMessage="Brak ulubionych wykrojów"
                    />
                  </Message.Header>
                  <p>
                    <FormattedMessage
                      id="favourites.empty.message"
                      defaultMessage="Dodaj wykroje do ulubionych, aby pojawiły się tutaj."
                    />
                  </p>
                </Message>
              ) : resultsCount === 0 ? (
                <Message info>
                  <Message.Header>
                    <FormattedMessage
                      id="favourites.filtered.empty.title"
                      defaultMessage="Brak wyników dla wybranych filtrów"
                    />
                  </Message.Header>
                  <p>
                    <FormattedMessage
                      id="favourites.filtered.empty.message"
                      defaultMessage="Zmień filtry, aby zobaczyć więcej ulubionych wykrojów."
                    />
                  </p>
                </Message>
              ) : (
                <Grid>
                  {filteredPatterns.map((pattern) => (
                    <Grid.Column
                      mobile={16}
                      tablet={8}
                      computer={4}
                      key={pattern._id}
                    >
                      <PatternBox pattern={pattern} />
                    </Grid.Column>
                  ))}
                </Grid>
              )}
            </main>
          </Grid.Column>
        </Grid>
      </Container>
    </div>
  );
};

export default Favourites;
